import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Palette, Loader2, Save, Sun, Moon, Monitor, Image as ImageIcon, Upload, Trash2 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import apiClient from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import { resizeImageToDataUrl } from '@/lib/imageUpload'

/**
 * Admin → Settings → Online Ordering (Appearance).
 *
 * Curated branding controls for the customer-facing storefront. Writes a small
 * set of `storefront_*` keys to app_settings; the storefront reads them at
 * runtime (GET /public/settings → ThemeSync) and DERIVES the rest of the
 * palette from the one brand colour, so a tenant can't produce an unreadable or
 * off-brand result. Leaving a field blank inherits the tenant's receipt
 * branding (the previous behaviour).
 */
const HEX = /^#[0-9a-fA-F]{6}$/
const DEFAULT_BRAND = '#16a34a'
const DEFAULT_ACCENT = '#f97316'
// Same raw-file ceiling as menu photos (ProductForm). The stored value is far
// smaller — resizeImageToDataUrl re-encodes at 1600px before saving.
const MAX_HERO_FILE_BYTES = 5 * 1024 * 1024

type ThemeMode = '' | 'system' | 'light' | 'dark'

interface StorefrontSocial {
  platform: string
  url: string
}
interface StorefrontConfig {
  hero_headline?: string
  hero_subcopy?: string
  about?: string
  font_heading?: string
  font_body?: string
  splash_phrase?: string
  socials?: StorefrontSocial[]
  layout?: { show_featured?: boolean; show_deals?: boolean }
}

// Mirror of storefront/src/lib/fonts.ts FONT_NAMES — keep in sync. '' = default (Inter).
const FONT_OPTIONS = ['', 'Inter', 'Poppins', 'Manrope', 'DM Sans', 'Sora', 'Nunito', 'Space Grotesk', 'Playfair Display']
// Mirror of the backend socialPlatforms allowlist (public_storefront.go).
const SOCIAL_PLATFORMS = ['instagram', 'facebook', 'tiktok', 'whatsapp', 'x', 'youtube', 'website']

function readConfig(v: unknown): StorefrontConfig {
  if (v && typeof v === 'object') return v as StorefrontConfig
  if (typeof v === 'string') {
    try {
      return JSON.parse(v) as StorefrontConfig
    } catch {
      return {}
    }
  }
  return {}
}

function str(d: Record<string, unknown> | undefined, key: string): string {
  const v = d?.[key]
  return typeof v === 'string' ? v : ''
}

function hexRgb(hex: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim())
  if (!m) return null
  const n = parseInt(m[1], 16)
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255]
}

// Contrast-safe foreground (mirrors the storefront's readableFg).
function fgFor(hex: string): string {
  const rgb = hexRgb(hex)
  if (!rgb) return '#ffffff'
  const luma = (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000
  return luma > 150 ? '#171717' : '#ffffff'
}

function darken(hex: string, amt = 0.14): string {
  const rgb = hexRgb(hex)
  if (!rgb) return hex
  const f = (c: number) => Math.max(0, Math.round(c * (1 - amt)))
  return `#${[f(rgb[0]), f(rgb[1]), f(rgb[2])].map((c) => c.toString(16).padStart(2, '0')).join('')}`
}

export function AdminStorefrontAppearance() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const settingsQuery = useQuery({
    queryKey: ['settings', 'all'],
    queryFn: () => apiClient.getAllSettings(),
  })
  const d = settingsQuery.data?.data as Record<string, unknown> | undefined

  // Storefront-specific values (empty ⇒ inherit receipt branding).
  const [brand, setBrand] = useState('')
  const [accent, setAccent] = useState('')
  const [logo, setLogo] = useState('')
  const [hero, setHero] = useState('')
  const [mode, setMode] = useState<ThemeMode>('')

  // Advanced customization (persisted as the single storefront_config blob).
  const [heroHeadline, setHeroHeadline] = useState('')
  const [heroSubcopy, setHeroSubcopy] = useState('')
  const [about, setAbout] = useState('')
  const [fontHeading, setFontHeading] = useState('')
  const [fontBody, setFontBody] = useState('')
  const [splashPhrase, setSplashPhrase] = useState('')
  const [showFeatured, setShowFeatured] = useState(true)
  const [showDeals, setShowDeals] = useState(true)
  const [socials, setSocials] = useState<StorefrontSocial[]>([])
  const [minOrder, setMinOrder] = useState('') // storefront_min_order_value (0/'' ⇒ no minimum)

  useEffect(() => {
    if (!d) return
    setBrand(str(d, 'storefront_theme_color'))
    setAccent(str(d, 'storefront_accent_color'))
    setLogo(str(d, 'storefront_logo_url'))
    setHero(str(d, 'storefront_hero_url'))
    const m = str(d, 'storefront_theme_mode')
    setMode(m === 'light' || m === 'dark' || m === 'system' ? m : '')

    const cfg = readConfig(d['storefront_config'])
    setHeroHeadline(cfg.hero_headline ?? '')
    setHeroSubcopy(cfg.hero_subcopy ?? '')
    setAbout(cfg.about ?? '')
    setFontHeading(cfg.font_heading ?? '')
    setFontBody(cfg.font_body ?? '')
    setSplashPhrase(cfg.splash_phrase ?? '')
    setShowFeatured(cfg.layout?.show_featured !== false)
    setShowDeals(cfg.layout?.show_deals !== false)
    setSocials(Array.isArray(cfg.socials) ? cfg.socials : [])
    const mv = d['storefront_min_order_value']
    setMinOrder(typeof mv === 'number' && mv > 0 ? String(mv) : '')
  }, [d])

  // Effective values for the live preview: storefront key → receipt fallback → default.
  const effBrand = (HEX.test(brand) ? brand : str(d, 'receipt_accent_color')) || DEFAULT_BRAND
  const effAccent = (HEX.test(accent) ? accent : '') || DEFAULT_ACCENT
  const effLogo = logo.trim() || str(d, 'receipt_logo_url')
  const previewDark = mode === 'dark'

  const brandFg = useMemo(() => fgFor(effBrand), [effBrand])
  const accentFg = useMemo(() => fgFor(effAccent), [effAccent])

  const brandInvalid = brand.trim() !== '' && !HEX.test(brand)
  const accentInvalid = accent.trim() !== '' && !HEX.test(accent)

  // Hero image upload → downscaled data URL (project convention: images live
  // inline in app_settings, no bucket). Public API + storefront already accept
  // data:image/ URLs, so only this input side is new.
  const heroFileRef = useRef<HTMLInputElement>(null)
  const [heroUploading, setHeroUploading] = useState(false)
  const heroIsUpload = hero.trim().startsWith('data:')

  const handleHeroFile = async (file: File | undefined) => {
    if (heroFileRef.current) heroFileRef.current.value = ''
    if (!file) return
    if (!file.type.startsWith('image/')) {
      toast({ title: 'Not an image', description: 'Choose a PNG, JPEG, WebP or SVG file.', variant: 'destructive' })
      return
    }
    if (file.size > MAX_HERO_FILE_BYTES) {
      toast({ title: 'Image too large', description: 'Choose a file under 5 MB.', variant: 'destructive' })
      return
    }
    setHeroUploading(true)
    try {
      setHero(await resizeImageToDataUrl(file, 1600, 0.82))
    } catch (err) {
      toast({
        title: 'Could not process image',
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      })
    } finally {
      setHeroUploading(false)
    }
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      // Empty string clears the key → storefront inherits receipt branding.
      await apiClient.updateSetting('storefront_theme_color', HEX.test(brand) ? brand : '')
      await apiClient.updateSetting('storefront_accent_color', HEX.test(accent) ? accent : '')
      await apiClient.updateSetting('storefront_logo_url', logo.trim())
      await apiClient.updateSetting('storefront_hero_url', hero.trim())
      await apiClient.updateSetting('storefront_theme_mode', mode)
      // Advanced content/fonts/layout in one consolidated blob.
      await apiClient.updateSetting('storefront_config', {
        hero_headline: heroHeadline.trim(),
        hero_subcopy: heroSubcopy.trim(),
        about: about.trim(),
        font_heading: fontHeading,
        font_body: fontBody,
        splash_phrase: splashPhrase.trim(),
        socials: socials
          .filter((s) => s.url.trim() !== '')
          .map((s) => ({ platform: s.platform, url: s.url.trim() })),
        layout: { show_featured: showFeatured, show_deals: showDeals },
      })
      // Minimum-order value gates checkout (server-enforced) + drives the cart nudge.
      await apiClient.updateSetting('storefront_min_order_value', Math.max(0, Number(minOrder) || 0))
    },
    onSuccess: () => {
      toast({ title: 'Storefront appearance saved' })
      queryClient.invalidateQueries({ queryKey: ['settings', 'all'] })
    },
    onError: (err) => {
      toast({ title: 'Could not save', description: err instanceof Error ? err.message : String(err), variant: 'destructive' })
    },
  })

  const canSave = !brandInvalid && !accentInvalid && !saveMutation.isPending

  return (
    <Card id="storefront-appearance" className="space-y-5 p-5">
      <div className="flex items-center gap-3 border-b border-border/60 pb-3">
        <Palette className="h-5 w-5 text-primary" aria-hidden />
        <div className="min-w-0 flex-1">
          <h3 className="text-sm font-semibold">Storefront appearance</h3>
          <p className="text-xs text-muted-foreground">
            Brand your online-ordering site. Leave a field blank to inherit your receipt branding. The full palette is
            derived from your brand colour, so it always stays readable.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        {/* ── Controls ── */}
        <div className="space-y-4">
          <ColorField
            label="Brand colour"
            hint="Header, buttons, highlights."
            value={brand}
            placeholder={str(d, 'receipt_accent_color') || DEFAULT_BRAND}
            onChange={setBrand}
            invalid={brandInvalid}
          />
          <ColorField
            label="Accent colour"
            hint="Deal badges + price emphasis (optional)."
            value={accent}
            placeholder={DEFAULT_ACCENT}
            onChange={setAccent}
            invalid={accentInvalid}
          />

          <div className="space-y-2">
            <Label htmlFor="sf-logo">Logo URL</Label>
            <Input
              id="sf-logo"
              type="url"
              inputMode="url"
              placeholder={str(d, 'receipt_logo_url') || 'https://…/logo.png'}
              value={logo}
              onChange={(e) => setLogo(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>Hero / banner image</Label>
            <input
              ref={heroFileRef}
              type="file"
              accept="image/png,image/jpeg,image/webp,image/svg+xml"
              className="hidden"
              onChange={(e) => handleHeroFile(e.target.files?.[0])}
            />
            {hero.trim() ? (
              <div className="flex items-start gap-3">
                <div className="aspect-[5/4] w-28 shrink-0 overflow-hidden rounded-md border border-border/60 bg-muted">
                  <img src={hero.trim()} alt="Hero preview" className="h-full w-full object-cover" />
                </div>
                <div className="space-y-1.5 pt-0.5">
                  <p className="text-xs text-muted-foreground">{heroIsUpload ? 'Uploaded image' : 'Linked image'}</p>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={heroUploading}
                      onClick={() => heroFileRef.current?.click()}
                    >
                      {heroUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                      Replace
                    </Button>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setHero('')}>
                      <Trash2 className="h-4 w-4" />
                      Remove
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <Button
                type="button"
                variant="outline"
                size="sm"
                disabled={heroUploading}
                onClick={() => heroFileRef.current?.click()}
              >
                {heroUploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                Upload image
              </Button>
            )}
            {!heroIsUpload && (
              <Input
                id="sf-hero"
                type="url"
                inputMode="url"
                placeholder="…or paste an image URL"
                value={hero}
                onChange={(e) => setHero(e.target.value)}
              />
            )}
            <p className="text-[11px] text-muted-foreground">
              Landscape photo of your food or space (~5:4) — shown beside the restaurant name. Blank uses a brand gradient.
            </p>
          </div>

          <div className="space-y-2">
            <Label>Default theme</Label>
            <div className="flex gap-2">
              {(
                [
                  { id: 'system', label: 'System', icon: Monitor },
                  { id: 'light', label: 'Light', icon: Sun },
                  { id: 'dark', label: 'Dark', icon: Moon },
                ] as const
              ).map((opt) => {
                const active = (mode || 'system') === opt.id
                const Icon = opt.icon
                return (
                  <Button
                    key={opt.id}
                    type="button"
                    variant={active ? 'default' : 'outline'}
                    size="sm"
                    className="flex-1 gap-1.5"
                    onClick={() => setMode(opt.id)}
                  >
                    <Icon className="h-4 w-4" />
                    {opt.label}
                  </Button>
                )
              })}
            </div>
            <p className="text-[11px] text-muted-foreground">A customer can still flip light/dark themselves; this is the default.</p>
          </div>
        </div>

        {/* ── Live preview ── */}
        <div className="space-y-2">
          <Label className="flex items-center gap-1.5">
            <ImageIcon className="h-3.5 w-3.5" /> Live preview
          </Label>
          <StorefrontPreview
            dark={previewDark}
            brand={effBrand}
            brandDark={darken(effBrand)}
            brandFg={brandFg}
            accent={effAccent}
            accentFg={accentFg}
            logo={effLogo}
            hero={hero.trim()}
            name={str(d, 'receipt_business_name') || str(d, 'restaurant_name') || 'Your restaurant'}
          />
        </div>
      </div>

      {/* ── Content, fonts & layout (storefront_config) ── */}
      <div className="space-y-5 border-t border-border/60 pt-5">
        <div>
          <h4 className="text-sm font-semibold">Content &amp; fonts</h4>
          <p className="text-xs text-muted-foreground">
            Headline, story, fonts, social links and which sections show on your storefront.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="sf-headline">Hero headline</Label>
            <Input
              id="sf-headline"
              maxLength={120}
              placeholder="Fresh, fast, unforgettable"
              value={heroHeadline}
              onChange={(e) => setHeroHeadline(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="sf-subcopy">Hero sub-copy</Label>
            <Input
              id="sf-subcopy"
              maxLength={240}
              placeholder="Order ahead and skip the wait."
              value={heroSubcopy}
              onChange={(e) => setHeroSubcopy(e.target.value)}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="sf-about">About / footer blurb</Label>
          <textarea
            id="sf-about"
            rows={3}
            maxLength={2000}
            placeholder="Tell customers who you are — shown in the storefront footer."
            value={about}
            onChange={(e) => setAbout(e.target.value)}
            className="flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
          />
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <FontSelect label="Heading font" value={fontHeading} onChange={setFontHeading} />
          <FontSelect label="Body font" value={fontBody} onChange={setFontBody} />
        </div>

        <div className="space-y-2">
          <div className="flex items-baseline justify-between">
            <Label htmlFor="sf-splash">Splash loading phrase</Label>
            <span className="text-[11px] tabular-nums text-muted-foreground">{splashPhrase.length}/40</span>
          </div>
          <Input
            id="sf-splash"
            maxLength={40}
            placeholder="firing up the tandoor…"
            value={splashPhrase}
            onChange={(e) => setSplashPhrase(e.target.value)}
          />
          <p className="text-[11px] text-muted-foreground">
            Shown on the launch screen while your store loads — a branded stand-in for “loading…” (e.g. a cookie
            shop might use “crumbling…”). Leave blank for none.
          </p>
        </div>

        <div className="grid gap-4 lg:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="sf-minorder">Minimum order value</Label>
            <Input
              id="sf-minorder"
              type="number"
              min={0}
              inputMode="numeric"
              placeholder="0 (no minimum)"
              value={minOrder}
              onChange={(e) => setMinOrder(e.target.value)}
            />
            <p className="text-[11px] text-muted-foreground">
              Customers see a “you’re Rs X away” nudge; checkout is blocked below this (server-enforced).
            </p>
          </div>
          <div className="space-y-2">
            <Label>Show sections</Label>
            <div className="flex flex-wrap gap-4 pt-1.5">
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showFeatured} onChange={(e) => setShowFeatured(e.target.checked)} />
                Featured rail
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input type="checkbox" checked={showDeals} onChange={(e) => setShowDeals(e.target.checked)} />
                Deals strip
              </label>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <Label>Social links</Label>
          <div className="space-y-2">
            {socials.map((s, i) => (
              <div key={i} className="flex items-center gap-2">
                <select
                  value={s.platform}
                  onChange={(e) =>
                    setSocials((arr) => arr.map((x, j) => (j === i ? { ...x, platform: e.target.value } : x)))
                  }
                  className="h-9 rounded-md border border-input bg-transparent px-2 text-sm"
                >
                  {SOCIAL_PLATFORMS.map((p) => (
                    <option key={p} value={p}>
                      {p}
                    </option>
                  ))}
                </select>
                <Input
                  type="url"
                  inputMode="url"
                  placeholder="https://…"
                  value={s.url}
                  onChange={(e) => setSocials((arr) => arr.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)))}
                  className="flex-1"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setSocials((arr) => arr.filter((_, j) => j !== i))}
                >
                  Remove
                </Button>
              </div>
            ))}
            {socials.length < 8 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setSocials((arr) => [...arr, { platform: 'instagram', url: '' }])}
              >
                + Add link
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end border-t border-border/60 pt-4">
        <Button type="button" onClick={() => saveMutation.mutate()} disabled={!canSave} className="gap-1.5">
          {saveMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          Save appearance
        </Button>
      </div>
    </Card>
  )
}

function FontSelect({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
        style={{ fontFamily: value ? `'${value}', sans-serif` : undefined }}
      >
        {FONT_OPTIONS.map((f) => (
          <option key={f || 'default'} value={f}>
            {f || 'Default (Inter)'}
          </option>
        ))}
      </select>
    </div>
  )
}

function ColorField({
  label,
  hint,
  value,
  placeholder,
  onChange,
  invalid,
}: {
  label: string
  hint: string
  value: string
  placeholder: string
  onChange: (v: string) => void
  invalid: boolean
}) {
  const swatch = HEX.test(value) ? value : HEX.test(placeholder) ? placeholder : '#16a34a'
  return (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${label} picker`}
          value={swatch}
          onChange={(e) => onChange(e.target.value)}
          className="h-9 w-10 shrink-0 cursor-pointer rounded-md border border-input bg-transparent p-1"
        />
        <Input
          value={value}
          placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className={invalid ? 'border-destructive focus-visible:ring-destructive' : 'font-mono'}
        />
      </div>
      <p className={`text-[11px] ${invalid ? 'text-destructive' : 'text-muted-foreground'}`}>
        {invalid ? 'Enter a 6-digit hex colour like #16a34a.' : hint}
      </p>
    </div>
  )
}

function StorefrontPreview({
  dark,
  brand,
  brandDark,
  brandFg,
  accent,
  accentFg,
  logo,
  hero,
  name,
}: {
  dark: boolean
  brand: string
  brandDark: string
  brandFg: string
  accent: string
  accentFg: string
  logo: string
  hero: string
  name: string
}) {
  const surface = dark ? '#18181b' : '#ffffff'
  const surfaceSoft = dark ? '#27272a' : '#f5f5f2'
  const ink = dark ? '#fafafa' : '#171717'
  const inkMuted = dark ? '#a1a1aa' : '#5a5a58'
  const line = dark ? '#27272a' : '#e6e6e2'
  const pageBg = dark ? '#0c0c0e' : '#fafaf8'

  return (
    <div className="overflow-hidden rounded-2xl border" style={{ borderColor: line, background: pageBg }}>
      {/* header */}
      <div className="flex items-center gap-2 px-3 py-2.5" style={{ background: surface, borderBottom: `1px solid ${line}` }}>
        {logo ? (
          <img src={logo} alt="" className="h-7 w-7 rounded-lg object-cover" />
        ) : (
          <span className="flex h-7 w-7 items-center justify-center rounded-lg text-xs font-bold" style={{ background: brand, color: brandFg }}>
            {name.charAt(0).toUpperCase()}
          </span>
        )}
        <span className="text-xs font-semibold" style={{ color: ink }}>{name}</span>
      </div>

      {/* hero */}
      <div
        className="relative px-3 py-4"
        style={
          hero
            ? { backgroundImage: `linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0.2)), url(${hero})`, backgroundSize: 'cover', backgroundPosition: 'center' }
            : { backgroundImage: `linear-gradient(135deg, ${brand}, ${brandDark})` }
        }
      >
        <div className="text-base font-extrabold" style={{ color: hero ? '#fff' : brandFg }}>{name}</div>
        <div className="mt-1.5 flex gap-1.5">
          {['Pickup', 'Delivery'].map((t) => (
            <span
              key={t}
              className="rounded-full px-2 py-0.5 text-[10px] font-semibold"
              style={{ background: hero ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.16)', color: hero ? '#fff' : brandFg }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>

      {/* menu card */}
      <div className="p-3">
        <div className="flex items-center gap-2.5 rounded-xl border p-2.5" style={{ background: surface, borderColor: line }}>
          <div className="h-12 w-12 shrink-0 rounded-lg" style={{ background: surfaceSoft }} />
          <div className="min-w-0 flex-1">
            <div className="text-xs font-semibold" style={{ color: ink }}>Buffalo Wings</div>
            <div className="truncate text-[10px]" style={{ color: inkMuted }}>Crispy chicken wings</div>
            <div className="mt-1 flex items-center justify-between">
              <span className="text-xs font-bold" style={{ color: ink }}>Rs 500</span>
              <span className="rounded-lg px-2.5 py-1 text-[10px] font-semibold" style={{ background: brand, color: brandFg }}>
                + Add
              </span>
            </div>
          </div>
        </div>
        <div className="mt-2 flex items-center gap-1.5">
          <span className="rounded-md px-1.5 py-0.5 text-[10px] font-bold" style={{ background: accent, color: accentFg }}>20% OFF</span>
          <span className="text-[10px]" style={{ color: inkMuted }}>Accent — deals & badges</span>
        </div>
      </div>
    </div>
  )
}
